'use client'

import { m } from 'motion/react'
import Link from 'next/link'

import { ChevronRightIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import { SPRING } from '@/lib/motion-utils'

interface TileCardProps {
  href: string
  title: string
  description: string
  /** The media layer (video/image) rendered behind the gradient + text. */
  children: React.ReactNode
  className?: string
  /** Opens the link in a new tab (for off-site tiles). */
  external?: boolean
  /** Override the default bottom gradient (e.g. a stronger scrim for video). */
  gradientClassName?: string
  onClick?: () => void
}

/**
 * Shared chrome for the home/craft "tiles" — a bordered, rounded card with a
 * hover-scale, a bottom gradient scrim, a chevron affordance, and a
 * title/description over a full-bleed media layer. Each tile only supplies its
 * href, copy, and media.
 */
export function TileCard({
  href,
  title,
  description,
  children,
  className,
  external,
  gradientClassName,
  onClick,
}: TileCardProps) {
  const externalProps = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {}

  return (
    <Link href={href} onClick={onClick} {...externalProps}>
      <m.div
        className={cn(
          'group relative overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700',
          className,
        )}
        whileHover={{ scale: 1.02 }}
        transition={SPRING.hover}
      >
        <div
          className={cn('scrim-to-t absolute inset-0 z-10', gradientClassName)}
        />

        <div className="ease-out-strong absolute top-4 right-4 z-20 size-5 text-white/70 drop-shadow-[0_1px_2px_rgb(0_0_0/0.6)] transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:text-white">
          <ChevronRightIcon className="stroke-current" />
        </div>

        <div className="relative size-full bg-zinc-100 dark:bg-zinc-800">
          <div className="relative z-20 flex h-full flex-col items-start justify-end p-4">
            <h3 className="text-lg font-medium text-white">{title}</h3>
            <p className="text-sm text-zinc-300">{description}</p>
          </div>

          <div className="absolute inset-0">{children}</div>
        </div>
      </m.div>
    </Link>
  )
}
